import { ShiftSummaryRecord } from '../services/shiftApi';
import { CurrentTicket, UrgentTicket } from '../types/shift';

export interface ShiftMetrics {
  shiftId: string;
  date: string;
  shift: string;
  onDutyTech: string;
  open: number;
  urgent: number;
  stale: number;
  carryOverRate: number;
  currentTickets: CurrentTicket[];
}

function parseJsonArray<T>(json: string | null | undefined): T[] {
  if (!json) return [];
  try {
    let parsed = JSON.parse(json);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

export function isStaleTicket(ticket: CurrentTicket, asOf: Date = new Date()): boolean {
  if (!ticket.firstAppearedDate) return false;
  const first = new Date(ticket.firstAppearedDate);
  if (Number.isNaN(first.getTime())) return false;
  const days = (asOf.getTime() - first.getTime()) / 86_400_000;
  return days >= 7;
}

export function computeShiftMetrics(record: ShiftSummaryRecord): ShiftMetrics {
  const current = parseJsonArray<CurrentTicket>(record.currentTicketsJson);
  const urgent = parseJsonArray<UrgentTicket>(record.urgentTicketsJson);
  const asOf = new Date(`${record.date}T00:00`);
  const validAsOf = Number.isNaN(asOf.getTime()) ? new Date() : asOf;
  const stale = current.filter((t) => isStaleTicket(t, validAsOf)).length;
  const carried = current.filter((t) => t.carriedForward).length;
  const carryOverRate = current.length > 0 ? Math.round((carried / current.length) * 100) : 0;
  return {
    shiftId: record.id,
    date: record.date,
    shift: record.shift,
    onDutyTech: record.onDutyTech,
    open: current.length,
    urgent: urgent.length,
    stale,
    carryOverRate,
    currentTickets: current,
  };
}

/**
 * Chronological (oldest -> newest) metrics for the last N shifts.
 * `records` is expected newest-first, as listShiftSummaries returns.
 */
export function lastNShiftMetrics(records: ShiftSummaryRecord[], n = 14): ShiftMetrics[] {
  return records.slice(0, n).map(computeShiftMetrics).reverse();
}

export interface SparklineSummary {
  values: number[];
  min: number;
  max: number;
  current: number;
}

export function sparklineSummary(values: number[]): SparklineSummary {
  if (values.length === 0) return { values: [], min: 0, max: 0, current: 0 };
  return {
    values,
    min: Math.min(...values),
    max: Math.max(...values),
    current: values[values.length - 1],
  };
}

/**
 * Returns per-customer count delta between the two most recent shifts.
 * null when no previous shift exists to compare against.
 */
export function customerDelta(
  latest: ShiftMetrics | undefined,
  previous: ShiftMetrics | undefined,
): Record<string, number | null> {
  const counts = (m: ShiftMetrics | undefined) => {
    const out: Record<string, number> = {};
    if (!m) return out;
    for (const t of m.currentTickets) {
      const c = t.customer || 'Unknown';
      out[c] = (out[c] || 0) + 1;
    }
    return out;
  };
  const latestCounts = counts(latest);
  const prevCounts = counts(previous);
  const all = new Set([...Object.keys(latestCounts), ...Object.keys(prevCounts)]);
  const out: Record<string, number | null> = {};
  for (const c of all) {
    out[c] = previous ? (latestCounts[c] ?? 0) - (prevCounts[c] ?? 0) : null;
  }
  return out;
}
